import React, { FC, useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';
import './platform-stats-user-experience.scss';

type UserBucket =
  | 'has_uses_but_is_enjoying'
  | 'has_uses_but_isnt_enjoying'
  | 'doesnt_have_uses';

const BUCKET_ORDER: UserBucket[] = [
  'has_uses_but_is_enjoying',
  'has_uses_but_isnt_enjoying',
  'doesnt_have_uses',
];

interface Frustration {
  type: string;
  expressed: boolean;
  dangerous: boolean;
}

interface FrustrationTally {
  expressed: number;
  inferred: number;
  dangerous: number;
}

interface CategoryTally {
  tasks: number;
  noCorrectionNeeded: number;
  lightCorrection: number;
  majorCorrection: number;
  unresolved: number;
  correctionRate: number | null;
}

interface FailureRecord {
  code: string;
  day: string;
  type: string;
  expressed: boolean;
  dangerous: boolean;
  description: string;
}

interface WindowScore {
  activeDays: number;
  distinctiveTasks: number;
  noCorrectionNeeded: number;
  lightCorrection: number;
  majorCorrection: number;
  unresolved: number;
  expressionsOfDelight: number;
  frustrations: Frustration[];
  performanceScore: number | null;
  enjoymentScore: number | null;
  bucket: UserBucket;
}

interface UserRow extends WindowScore {
  userId: string;
  name: string | null;
  companyId: number;
  userMessageCount: number;
  loggedUserMessageCount: number;
  frustrationTally: Record<string, FrustrationTally>;
}

interface DayRow {
  day: string;
  userMessageCount: number;
  assistantMessageCount: number;
  loggedUserMessageCount: number;
  distinctiveTasks: number;
  noCorrectionNeeded: number;
  lightCorrection: number;
  majorCorrection: number;
  unresolved: number;
  expressionsOfDelight: number;
  frustrations: Frustration[];
  performanceScore: number | null;
  modelName: string | null;
  classifiedAt: string;
}

interface ReportConfig {
  lightCorrectionCredit: number;
  delightBonus: number;
  minTasksForUses: number;
  enjoyingThreshold: number;
  bucketLabels: Record<UserBucket, string>;
  frustrationLabels: Record<string, string>;
  frustrationDefinitions?: Record<string, string>;
  categoryLabels: Record<string, string>;
  categoryDefinitions?: Record<string, string>;
}

interface ReportResponse {
  success: boolean;
  from: string;
  to: string;
  users: UserRow[];
  buckets: Record<UserBucket, number>;
  frustrationTally: Record<string, FrustrationTally>;
  dangerousFailures: number;
  categories: {
    all: Record<string, CategoryTally>;
    byBucket: Record<UserBucket, Record<string, CategoryTally>>;
  };
  analysedDays: number;
  config: ReportConfig;
}

interface FailuresResponse {
  success: boolean;
  failures: FailureRecord[];
}

interface DetailResponse {
  success: boolean;
  user: { userId: string; name: string | null } | null;
  days: DayRow[];
  window: WindowScore;
}

interface RunResponse {
  success: boolean;
  jobId?: string;
}

const toDay = (d: Date) => d.toISOString().slice(0, 10);

const defaultWindow = () => {
  const to = new Date();
  to.setUTCDate(to.getUTCDate() - 1);
  const from = new Date(to);
  from.setUTCDate(from.getUTCDate() - 6);
  return { from: toDay(from), to: toDay(to) };
};

const errorMessage = (err: unknown, fallback: string): string => {
  const axiosErr = err as { response?: { data?: { error?: string } } };
  return axiosErr?.response?.data?.error || fallback;
};

const score = (n: number | null) => (n === null ? '—' : String(n));

const Coverage: FC<{ seen: number; logged: number }> = ({ seen, logged }) =>
  logged > seen ? (
    <span
      className='ux-partial'
      title='Thread history had been trimmed before classification; only part of the day was assessed'
    >
      {seen}/{logged}
    </span>
  ) : (
    <>{seen}</>
  );

const FrustrationChips: FC<{
  tally: Record<string, FrustrationTally>;
  labels: Record<string, string>;
  definitions?: Record<string, string>;
}> = ({ tally, labels, definitions }) => {
  const entries = Object.entries(tally).sort(
    (a, b) => b[1].expressed + b[1].inferred - (a[1].expressed + a[1].inferred),
  );
  if (entries.length === 0) return <span className='ux-muted'>none</span>;
  return (
    <div className='ux-chips'>
      {entries.map(([type, t]) => (
        <span
          key={type}
          className='ux-chip'
          title={[
            definitions?.[type],
            `${t.expressed} expressed by the user, ${t.inferred} inferred from behaviour${
              t.dangerous > 0 ? `, ${t.dangerous} dangerous` : ''
            }`,
          ]
            .filter(Boolean)
            .join('\n\n')}
        >
          {labels[type] ?? type}
          <span className='ux-chip-count'>
            {t.expressed}
            {t.inferred > 0 && <em> +{t.inferred}</em>}
            {t.dangerous > 0 && (
              <b className='ux-danger' title='Dangerous'>
                {' '}
                ⚠{t.dangerous}
              </b>
            )}
          </span>
        </span>
      ))}
    </div>
  );
};

const tallyDay = (fs: Frustration[]): Record<string, FrustrationTally> => {
  const out: Record<string, FrustrationTally> = {};
  for (const f of fs) {
    const t = (out[f.type] ??= { expressed: 0, inferred: 0, dangerous: 0 });
    if (f.expressed) t.expressed += 1;
    else t.inferred += 1;
    if (f.dangerous) t.dangerous += 1;
  }
  return out;
};

const rate = (n: number | null) => (n === null ? '—' : `${n}%`);

/**
 * Correction rate per task category, for everyone and split by bucket so
 * category patterns of enjoying vs not-enjoying users can be compared.
 */
const CategoryTable: FC<{
  categories: ReportResponse['categories'];
  labels: Record<string, string>;
  definitions?: Record<string, string>;
  bucketLabels: Record<UserBucket, string>;
}> = ({ categories, labels, definitions, bucketLabels }) => {
  const rows = Object.entries(categories.all)
    .filter(([, t]) => t.tasks > 0)
    .sort((a, b) => b[1].tasks - a[1].tasks);
  if (rows.length === 0)
    return <span className='ux-muted'>No tasks in this window.</span>;
  const enjoying = categories.byBucket.has_uses_but_is_enjoying;
  const notEnjoying = categories.byBucket.has_uses_but_isnt_enjoying;
  return (
    <div className='ux-table-wrap'>
      <table className='ux-table'>
        <thead>
          <tr>
            <th>Category</th>
            <th className='num'>Tasks</th>
            <th className='num'>No corr.</th>
            <th className='num'>Light</th>
            <th className='num'>Major</th>
            <th className='num'>Unresolved</th>
            <th
              className='num'
              title='(major + unresolved) / tasks, all users; light corrections excluded'
            >
              Corr. rate
            </th>
            <th
              className='num'
              title={`Correction rate among ${bucketLabels.has_uses_but_is_enjoying}`}
            >
              Enjoying
            </th>
            <th
              className='num'
              title={`Correction rate among ${bucketLabels.has_uses_but_isnt_enjoying}`}
            >
              Not enjoying
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([cat, t]) => (
            <tr key={cat} title={definitions?.[cat]}>
              <td>{labels[cat] ?? cat}</td>
              <td className='num'>{t.tasks}</td>
              <td className='num'>{t.noCorrectionNeeded}</td>
              <td className='num'>{t.lightCorrection}</td>
              <td className='num'>{t.majorCorrection}</td>
              <td className='num'>{t.unresolved}</td>
              <td className='num'>
                <strong>{rate(t.correctionRate)}</strong>
              </td>
              <td className='num'>
                {rate(enjoying[cat]?.correctionRate ?? null)}
                <span className='ux-muted'> /{enjoying[cat]?.tasks ?? 0}</span>
              </td>
              <td className='num'>
                {rate(notEnjoying[cat]?.correctionRate ?? null)}
                <span className='ux-muted'>
                  {' '}
                  /{notEnjoying[cat]?.tasks ?? 0}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/**
 * Anonymised failure list: opaque code + one-line description, no user.
 * Quote the code to look the exchange up with
 * scripts/user-experience-failure-lookup.ts.
 */
const FailuresPanel: FC<{
  failures: FailureRecord[];
  labels: Record<string, string>;
}> = ({ failures, labels }) => {
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [dangerousOnly, setDangerousOnly] = useState(false);
  const types = Array.from(new Set(failures.map((f) => f.type))).sort();
  const shown = failures.filter(
    (f) =>
      (typeFilter === 'all' || f.type === typeFilter) &&
      (!dangerousOnly || f.dangerous),
  );
  return (
    <>
      <div className='ux-failures-filters'>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <option value='all'>All types</option>
          {types.map((t) => (
            <option key={t} value={t}>
              {labels[t] ?? t}
            </option>
          ))}
        </select>
        <label>
          <input
            type='checkbox'
            checked={dangerousOnly}
            onChange={(e) => setDangerousOnly(e.target.checked)}
          />
          Dangerous only
        </label>
        <span className='ux-muted'>
          {shown.length} of {failures.length}
        </span>
      </div>
      {shown.length === 0 ? (
        <span className='ux-muted'>No failures match.</span>
      ) : (
        <ul className='ux-failures'>
          {shown.map((f) => (
            <li
              key={f.code}
              className={`ux-failure ${f.dangerous ? 'dangerous' : ''}`}
            >
              <code className='ux-failure-code'>{f.code}</code>
              <span className='ux-failure-day'>{f.day}</span>
              <span className='ux-chip'>{labels[f.type] ?? f.type}</span>
              {!f.expressed && (
                <span className='ux-muted' title='Inferred from behaviour'>
                  inferred
                </span>
              )}
              {f.dangerous && (
                <span
                  className='ux-pill ux-pill-danger'
                  title='A real-world action was or could have been taken wrongly'
                >
                  Dangerous
                </span>
              )}
              <span className='ux-failure-desc'>{f.description}</span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
};

/**
 * Platform Stats "User Experience" tab: whole-day LLM assessments of each
 * user's exchange with their assistant, rolled up over a window into an
 * assistant performance score, an enjoyment score and the
 * Has-Uses / Enjoying buckets. Drill into a user for the day rows.
 */
const PlatformStatsUserExperience: FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [{ from, to }, setWindow] = useState(defaultWindow);
  const [report, setReport] = useState<ReportResponse | null>(null);
  const [failures, setFailures] = useState<FailureRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bucketFilter, setBucketFilter] = useState<UserBucket | 'all'>('all');

  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<DetailResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [runDay, setRunDay] = useState(() => defaultWindow().to);
  const [running, setRunning] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const opts = {
        params: { from, to },
        headers: { Authorization: `Bearer ${token}` },
      };
      const [res, failuresRes] = await Promise.all([
        api.get<ReportResponse>('/platform-stats/user-experience', opts),
        api.get<FailuresResponse>(
          '/platform-stats/user-experience/failures',
          opts,
        ),
      ]);
      setReport(res.data);
      setFailures(failuresRes.data.failures);
    } catch (err) {
      setError(errorMessage(err, 'Failed to load user experience report'));
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, from, to]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!selected) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setDetailLoading(true);
      try {
        const token = await getAccessTokenSilently();
        const res = await api.get<DetailResponse>(
          `/platform-stats/user-experience/users/${encodeURIComponent(selected)}`,
          {
            params: { from, to },
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (!cancelled) setDetail(res.data);
      } catch (err) {
        if (!cancelled) setError(errorMessage(err, 'Failed to load user days'));
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selected, from, to, getAccessTokenSilently]);

  const enqueueRun = async () => {
    setRunning(true);
    setNotice(null);
    try {
      const token = await getAccessTokenSilently();
      const res = await api.post<RunResponse>(
        '/platform-stats/user-experience/run',
        { day: runDay, force: true },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setNotice(
        `Queued analysis for ${runDay} (job ${res.data.jobId ?? '?'}). Refresh in a few minutes.`,
      );
    } catch (err) {
      setNotice(errorMessage(err, 'Failed to queue analysis'));
    } finally {
      setRunning(false);
    }
  };

  const config = report?.config;
  const users = (report?.users ?? []).filter(
    (u) => bucketFilter === 'all' || u.bucket === bucketFilter,
  );
  const total = report ? report.users.length : 0;

  return (
    <div className='ux-tab'>
      <div className='ux-toolbar'>
        <label>
          From
          <input
            type='date'
            value={from}
            max={to}
            onChange={(e) =>
              e.target.value && setWindow({ from: e.target.value, to })
            }
          />
        </label>
        <label>
          To
          <input
            type='date'
            value={to}
            min={from}
            onChange={(e) =>
              e.target.value && setWindow({ from, to: e.target.value })
            }
          />
        </label>
        <div className='ux-toolbar-spacer' />
        <label>
          Re-run day
          <input
            type='date'
            value={runDay}
            onChange={(e) => e.target.value && setRunDay(e.target.value)}
          />
        </label>
        <button
          type='button'
          className='ux-btn'
          onClick={enqueueRun}
          disabled={running}
        >
          {running ? 'Queuing…' : 'Queue analysis'}
        </button>
      </div>
      {notice && <div className='ux-notice'>{notice}</div>}
      {error && <div className='msg error'>{error}</div>}

      {loading && !report ? (
        <div className='loading'>Loading user experience…</div>
      ) : report && config ? (
        <>
          <div className='ux-buckets'>
            {BUCKET_ORDER.map((b) => (
              <button
                type='button'
                key={b}
                className={`ux-bucket ux-bucket-${b} ${
                  bucketFilter === b ? 'active' : ''
                }`}
                onClick={() =>
                  setBucketFilter((cur) => (cur === b ? 'all' : b))
                }
              >
                <span className='ux-bucket-count'>{report.buckets[b]}</span>
                <span className='ux-bucket-label'>
                  {config.bucketLabels[b]}
                </span>
                <span className='ux-bucket-share'>
                  {total ? Math.round((100 * report.buckets[b]) / total) : 0}%
                </span>
              </button>
            ))}
            <div className='ux-bucket ux-bucket-meta'>
              <span className='ux-bucket-count'>{report.analysedDays}</span>
              <span className='ux-bucket-label'>user-days analysed</span>
              <span className='ux-bucket-share'>
                {from} → {to}
              </span>
            </div>
            <div
              className={`ux-bucket ux-bucket-meta ${
                report.dangerousFailures > 0 ? 'ux-bucket-danger' : ''
              }`}
              title='Frustrations where a real-world action (booking, send, payment, calendar, invite) was or could have been taken wrongly'
            >
              <span className='ux-bucket-count'>
                {report.dangerousFailures}
              </span>
              <span className='ux-bucket-label'>dangerous failures</span>
              <span className='ux-bucket-share'>
                of{' '}
                {Object.values(report.frustrationTally).reduce(
                  (n, t) => n + t.expressed + t.inferred,
                  0,
                )}{' '}
                frustrations
              </span>
            </div>
          </div>

          <div className='ux-card'>
            <div className='ux-card-head'>
              <h3>Frustrations encountered</h3>
              <span className='ux-muted'>
                count = expressed by the user, <em>+n</em> = inferred from
                behaviour
              </span>
            </div>
            <FrustrationChips
              tally={report.frustrationTally}
              labels={config.frustrationLabels}
              definitions={config.frustrationDefinitions}
            />
          </div>

          <div className='ux-card'>
            <div className='ux-card-head'>
              <h3>Correction rate by task category</h3>
              <span className='ux-muted'>
                corr. rate = (major + unresolved) / tasks (light excluded);
                bucket columns show rate /tasks for that bucket's users
              </span>
            </div>
            <CategoryTable
              categories={report.categories}
              labels={config.categoryLabels}
              definitions={config.categoryDefinitions}
              bucketLabels={config.bucketLabels}
            />
          </div>

          <div className='ux-card'>
            <div className='ux-card-head'>
              <h3>
                Failures <span className='ux-muted'>{failures.length}</span>
              </h3>
              <span className='ux-muted'>
                anonymised, newest first — quote the code to have the exchange
                looked up
              </span>
            </div>
            <FailuresPanel
              failures={failures}
              labels={config.frustrationLabels}
            />
          </div>

          <div className='ux-card'>
            <div className='ux-card-head'>
              <h3>
                Users{' '}
                <span className='ux-muted'>
                  {users.length}
                  {bucketFilter !== 'all' ? ` of ${total}` : ''}
                </span>
              </h3>
              <span className='ux-muted'>
                Performance = 100 × (no correction +{' '}
                {config.lightCorrectionCredit} × light) / tasks, +
                {config.delightBonus} per delight. Enjoyment = 100 × (tasks −
                major − unresolved − frustrations + delight) / tasks. Enjoying ≥{' '}
                {config.enjoyingThreshold}; has uses ≥ {config.minTasksForUses}{' '}
                tasks.
              </span>
            </div>
            <div className='ux-table-wrap'>
              <table className='ux-table'>
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Bucket</th>
                    <th className='num'>Days</th>
                    <th className='num' title='User messages assessed / logged'>
                      Msgs
                    </th>
                    <th className='num'>Tasks</th>
                    <th className='num'>No corr.</th>
                    <th className='num'>Light</th>
                    <th className='num'>Major</th>
                    <th className='num'>Unresolved</th>
                    <th className='num'>Delight</th>
                    <th className='num'>Frustr.</th>
                    <th className='num'>Perf.</th>
                    <th className='num'>Enjoy.</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan={13} className='ux-empty'>
                        No analysed user-days in this window.
                      </td>
                    </tr>
                  ) : (
                    users.map((u) => (
                      <tr
                        key={u.userId}
                        className={`ux-row ${
                          selected === u.userId ? 'selected' : ''
                        }`}
                        onClick={() =>
                          setSelected((cur) =>
                            cur === u.userId ? null : u.userId,
                          )
                        }
                      >
                        <td>
                          <div className='ux-user-name'>
                            {u.name || u.userId}
                          </div>
                          <div className='ux-user-id'>{u.userId}</div>
                        </td>
                        <td>
                          <span className={`ux-pill ux-pill-${u.bucket}`}>
                            {config.bucketLabels[u.bucket]}
                          </span>
                        </td>
                        <td className='num'>{u.activeDays}</td>
                        <td className='num'>
                          <Coverage
                            seen={u.userMessageCount}
                            logged={u.loggedUserMessageCount}
                          />
                        </td>
                        <td className='num'>{u.distinctiveTasks}</td>
                        <td className='num'>{u.noCorrectionNeeded}</td>
                        <td className='num'>{u.lightCorrection}</td>
                        <td className='num'>{u.majorCorrection}</td>
                        <td className='num'>{u.unresolved}</td>
                        <td className='num'>{u.expressionsOfDelight}</td>
                        <td className='num'>{u.frustrations.length}</td>
                        <td className='num'>{score(u.performanceScore)}</td>
                        <td className='num'>{score(u.enjoymentScore)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {selected && (
            <div className='ux-card'>
              <div className='ux-card-head'>
                <h3>
                  {detail?.user?.name || selected}{' '}
                  <span className='ux-muted'>day by day</span>
                </h3>
                <button
                  type='button'
                  className='ux-btn ux-btn-ghost'
                  onClick={() => setSelected(null)}
                >
                  Close
                </button>
              </div>
              {detailLoading && !detail ? (
                <div className='loading'>Loading days…</div>
              ) : detail ? (
                <>
                  <div className='ux-detail-summary'>
                    <span>
                      <strong>{detail.window.distinctiveTasks}</strong> tasks
                    </span>
                    <span>
                      performance{' '}
                      <strong>{score(detail.window.performanceScore)}</strong>
                    </span>
                    <span>
                      enjoyment{' '}
                      <strong>{score(detail.window.enjoymentScore)}</strong>
                    </span>
                    <FrustrationChips
                      tally={tallyDay(detail.window.frustrations)}
                      labels={config.frustrationLabels}
                      definitions={config.frustrationDefinitions}
                    />
                  </div>
                  <div className='ux-table-wrap'>
                    <table className='ux-table'>
                      <thead>
                        <tr>
                          <th>Day</th>
                          <th
                            className='num'
                            title='User messages assessed / logged'
                          >
                            User msgs
                          </th>
                          <th className='num'>Asst msgs</th>
                          <th className='num'>Tasks</th>
                          <th className='num'>No corr.</th>
                          <th className='num'>Light</th>
                          <th className='num'>Major</th>
                          <th className='num'>Unresolved</th>
                          <th className='num'>Delight</th>
                          <th>Frustrations</th>
                          <th className='num'>Perf.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.days.length === 0 ? (
                          <tr>
                            <td colSpan={11} className='ux-empty'>
                              No analysed days in this window.
                            </td>
                          </tr>
                        ) : (
                          detail.days.map((d) => (
                            <tr key={d.day}>
                              <td>{d.day}</td>
                              <td className='num'>
                                <Coverage
                                  seen={d.userMessageCount}
                                  logged={d.loggedUserMessageCount}
                                />
                              </td>
                              <td className='num'>{d.assistantMessageCount}</td>
                              <td className='num'>{d.distinctiveTasks}</td>
                              <td className='num'>{d.noCorrectionNeeded}</td>
                              <td className='num'>{d.lightCorrection}</td>
                              <td className='num'>{d.majorCorrection}</td>
                              <td className='num'>{d.unresolved}</td>
                              <td className='num'>{d.expressionsOfDelight}</td>
                              <td>
                                <FrustrationChips
                                  tally={tallyDay(d.frustrations)}
                                  labels={config.frustrationLabels}
                                  definitions={config.frustrationDefinitions}
                                />
                              </td>
                              <td className='num'>
                                {score(d.performanceScore)}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : null}
            </div>
          )}
        </>
      ) : null}
    </div>
  );
};

export default PlatformStatsUserExperience;
